'use client';

import { useMemo } from 'react';
import { keepPreviousData, useQuery, type UseQueryResult } from '@tanstack/react-query';
import { useCellServiceSession } from '@/hooks/useCellServiceSession';
import { mobileGraphql, mobileKeys, type MobileUiConfigResponse } from '@/shared/mobile-service/api/cors-mobile-api';
import {
  applyColumnLabels,
  buildFacetQuery,
  parseFacetResponse,
  planArtifactFacets,
  type FacetPlan,
  type FacetResult,
} from '../lib/build-facet-query';
import { resolveArtifactColumns } from '../lib/columns';
import { filterableFieldAllowlist, type FilterState } from '../lib/where';

export interface UseArtifactFacetsResult {
  plan: FacetPlan;
  query: UseQueryResult<FacetResult, Error>;
}

/**
 * Value counts and a time range for the artifact's facetable fields.
 *
 * Counts exclude each facet's own filter (see `whereExcluding`), so the whole
 * rail is one batched document rather than one request per chip group. Previous
 * data is kept across filter changes so chips don't disappear and re-flow while
 * the next counts land.
 */
export function useArtifactFacets(args: {
  mobileAcquisitionId: number;
  graphqlType: string;
  uiConfig: MobileUiConfigResponse | undefined;
  filters: FilterState;
  visibleFields?: readonly string[];
  enabled?: boolean;
}): UseArtifactFacetsResult {
  const { mobileAcquisitionId, graphqlType, uiConfig, filters, visibleFields } = args;
  const { cellId, accessToken, enabled: sessionEnabled } = useCellServiceSession();

  const artifact = uiConfig?.artifacts[graphqlType];

  const plan = useMemo(() => {
    const base = planArtifactFacets(artifact, visibleFields);
    if (!uiConfig) return base;
    const resolved = resolveArtifactColumns(graphqlType, uiConfig);
    return resolved ? applyColumnLabels(base, resolved.columns) : base;
  }, [artifact, uiConfig, graphqlType, visibleFields]);

  const built = useMemo(() => {
    if (!artifact) return null;
    return buildFacetQuery(
      artifact.front_end?.graphql_root_query ?? null,
      artifact.endpoint,
      plan,
      {
        mobileAcquisitionId,
        filters,
        allowedFields: filterableFieldAllowlist(artifact.capabilities?.filterable_fields),
      },
    );
  }, [artifact, plan, mobileAcquisitionId, filters]);

  const query = useQuery<FacetResult, Error>({
    queryKey: mobileKeys.artifactFacets(cellId, mobileAcquisitionId, graphqlType, {
      filters,
      fields: plan.fields.map((f) => f.field),
      range: plan.range?.field ?? null,
    }),
    enabled: sessionEnabled && (args.enabled ?? true) && Boolean(built),
    placeholderData: keepPreviousData,
    queryFn: async ({ signal }) => {
      const data = await mobileGraphql<Record<string, unknown>>({
        cellId: cellId as string,
        accessToken: accessToken as string,
        endpoint: (built as NonNullable<typeof built>).endpoint,
        query: (built as NonNullable<typeof built>).query,
        signal,
      });
      return parseFacetResponse(data, plan);
    },
  });

  return { plan, query };
}
